import React, { useState, useEffect, useRef } from 'react';

// ─── Field Wrapper ─────────────────────────────────────────────────────────────
export const Field = ({ label, error, required, hint, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {label && (
      <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', letterSpacing: 0.2 }}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
        {hint && (
          <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6, fontSize: 11 }}>
            {hint}
          </span>
        )}
      </label>
    )}
    {children}
    {error && (
      <span style={{ fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 4 }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        {error}
      </span>
    )}
  </div>
);

const inputBase = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1.5px solid #e5e7eb',
  fontSize: 16,
  color: '#111827',
  background: '#fff',
  outline: 'none',
  transition: 'border-color 0.15s',
  boxSizing: 'border-box',
};

const inputError = { borderColor: '#fca5a5', background: '#fff7f7' };

// ─── Text Input ────────────────────────────────────────────────────────────────
export const Input = React.forwardRef(({ error, ...props }, ref) => (
  <input
    ref={ref}
    style={{ ...inputBase, ...(error ? inputError : {}) }}
    onFocus={(e) => { e.target.style.borderColor = error ? '#ef4444' : '#6366f1'; }}
    onBlur={(e) => { e.target.style.borderColor = error ? '#fca5a5' : '#e5e7eb'; }}
    {...props}
  />
));
Input.displayName = 'Input';

// ─── Textarea ─────────────────────────────────────────────────────────────────
export const Textarea = React.forwardRef(({ error, rows = 3, ...props }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    style={{ ...inputBase, resize: 'vertical', ...(error ? inputError : {}) }}
    onFocus={(e) => { e.target.style.borderColor = error ? '#ef4444' : '#6366f1'; }}
    onBlur={(e) => { e.target.style.borderColor = error ? '#fca5a5' : '#e5e7eb'; }}
    {...props}
  />
));
Textarea.displayName = 'Textarea';

// ─── Number Input ─────────────────────────────────────────────────────────────
export const NumberInput = React.forwardRef(({ error, min = 1, onChange, ...props }, ref) => (
  // type="text" + inputMode="numeric" (как в поле «Количество мест» EventForm):
  // цифровая клавиатура на мобильном, но без спиннеров и инкремента колёсиком в браузере.
  // Нецифровые символы вырезаются до вызова пользовательского onChange.
  <input
    ref={ref}
    type="text"
    inputMode="numeric"
    pattern="[0-9]*"
    min={min}
    style={{ ...inputBase, ...(error ? inputError : {}) }}
    onChange={onChange ? (e) => {
      e.target.value = e.target.value.replace(/[^0-9]/g, '');
      onChange(e);
    } : undefined}
    onFocus={(e) => { e.target.style.borderColor = error ? '#ef4444' : '#6366f1'; }}
    onBlur={(e) => { e.target.style.borderColor = error ? '#fca5a5' : '#e5e7eb'; }}
    {...props}
  />
));
NumberInput.displayName = 'NumberInput';

// ─── Button ───────────────────────────────────────────────────────────────────
const buttonVariants = {
  primary: {
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    color: '#fff',
    border: 'none',
    boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
  },
  secondary: {
    background: '#f3f4f6',
    color: '#374151',
    border: '1.5px solid #e5e7eb',
    boxShadow: 'none',
  },
  danger: {
    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
    color: '#fff',
    border: 'none',
    boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
  },
  ghost: {
    background: 'transparent',
    color: '#6366f1',
    border: '1.5px solid #e0e7ff',
    boxShadow: 'none',
  },
};

export const Button = ({ variant = 'primary', loading, disabled, children, fullWidth, style: extraStyle, ...props }) => {
  const v = buttonVariants[variant] || buttonVariants.primary;
  return (
    <button
      disabled={disabled || loading}
      style={{
        ...v,
        padding: '11px 20px',
        borderRadius: 10,
        fontSize: 15,
        fontWeight: 600,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.65 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: fullWidth ? '100%' : 'auto',
        transition: 'opacity 0.15s, transform 0.1s',
        ...extraStyle,
      }}
      onMouseDown={(e) => { if (!disabled && !loading) e.currentTarget.style.transform = 'scale(0.97)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      {...props}
    >
      {loading && (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
          </path>
        </svg>
      )}
      {children}
    </button>
  );
};

// ─── Page Error Message ────────────────────────────────────────────────────────
export const PageError = ({ message, onRetry }) => (
  <div style={{
    margin: '24px 16px', padding: '16px', borderRadius: 12,
    background: '#fee2e2', border: '1.5px solid #fca5a5', color: '#b91c1c',
    fontSize: 14, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
      </svg>
      Ошибка
    </div>
    <span>{message}</span>
    {onRetry && (
      <button onClick={onRetry} style={{
        background: '#dc2626', color: '#fff', border: 'none',
        borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
      }}>Повторить</button>
    )}
  </div>
);

// ─── Alert Modal ──────────────────────────────────────────────────────────────
/**
 * Модальное окно-уведомление, которое пользователь должен явно закрыть.
 * Используется для важных SSE-событий (конфликт матча, смена настроек турнира).
 *
 * Props:
 *   title    — заголовок (строка)
 *   message  — текст уведомления (строка)
 *   icon     — emoji или строка перед заголовком (опционально)
 *   onClose  — колбэк закрытия (обязательный)
 */
export const AlertModal = ({ title, message, icon, onClose }) => {
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '0 16px',
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
          opacity: shown ? 1 : 0, transition: 'opacity 0.2s ease',
        }}
      />

      {/* Карточка */}
      <div style={{
        position: 'relative', width: '100%', maxWidth: 340,
        background: '#fff', borderRadius: 20,
        padding: '24px 20px 20px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        opacity: shown ? 1 : 0,
        transform: shown ? 'scale(1)' : 'scale(0.92)',
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        willChange: 'transform, opacity',
        textAlign: 'center',
      }}>
        {icon && (
          <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
        )}
        {title && (
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
            {title}
          </div>
        )}
        <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.5, marginBottom: 20 }}>
          {message}
        </div>
        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '13px', borderRadius: 12, border: 'none',
            background: 'linear-gradient(135deg, #d97706, #b45309)',
            color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Понятно
        </button>
      </div>
    </div>
  );
};

// ─── Loading Spinner ───────────────────────────────────────────────────────────
export const Spinner = ({ text = 'Загрузка...' }) => (
  <div style={{
    minHeight: '60vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 16, color: '#6b7280',
  }}>
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
      </path>
    </svg>
    <span style={{ fontSize: 15, fontWeight: 500 }}>{text}</span>
  </div>
);

// ─── Утилиты для наивных дат (Стратегия A — без таймзоны) ────────────────────

const MONTHS_RU = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const DAYS_SHORT_RU = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

export function pad2(n) { return String(n).padStart(2, '0'); }

// "YYYY-MM-DDTHH:mm:ss" → { year, month, day, hour, minute }
export function parseNaive(str) {
  if (!str) return null;
  const [datePart, timePart] = str.split('T');
  if (!datePart) return null;
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour = 0, minute = 0] = (timePart || '').split(':').map(Number);
  return { year, month, day, hour, minute };
}

// { year, month, day, hour, minute } → "YYYY-MM-DDTHH:mm:ss"
export function formatNaive({ year, month, day, hour, minute }) {
  return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}:00`;
}

// Валидация строки времени "HH:MM" введённой пользователем
function parseTimeInput(raw) {
  const match = raw.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { hour: h, minute: m };
}

// Сетка дней месяца (пн-вс)
function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month - 1, 1);
  let startDow = firstDay.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1;
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

// Форматирование для плашки просмотра в ViewEventPage
export function formatDateDisplay(eventDate, eventEndDate) {
  if (!eventDate) return null;
  const start = parseNaive(eventDate);
  if (!start) return null;
  const dateStr = `${pad2(start.day)}.${pad2(start.month)}.${start.year}`;
  const timeStr = `${pad2(start.hour)}:${pad2(start.minute)}`;
  if (eventEndDate) {
    const end = parseNaive(eventEndDate);
    if (end) {
      const endTimeStr = `${pad2(end.hour)}:${pad2(end.minute)}`;
      if (end.year === start.year && end.month === start.month && end.day === start.day) {
        return { date: dateStr, time: `${timeStr} – ${endTimeStr}` };
      }
      const endDateStr = `${pad2(end.day)}.${pad2(end.month)}.${end.year}`;
      return { date: dateStr, time: `${timeStr} – ${endDateStr} ${endTimeStr}` };
    }
  }
  return { date: dateStr, time: timeStr };
}

// ─── DateTimePicker ───────────────────────────────────────────────────────────
//
// UX-поток:
//   1. Клик → открывается календарь (или сразу шаг времени если значение уже есть)
//   2. Выбор дня → переходим на шаг времени
//   3. Большое поле ввода "17:30" — пишем вручную, автовставка ":"
//      Ниже — чипы-shortcuts :00 и :30 для каждого из 12 ближайших часов
//   4. Enter или кнопка "Готово" → фиксируем, закрываем
//   5. Крестик → сброс значения

export const DateTimePicker = ({ value, onChange, error, placeholder = 'Не указано', style: triggerStyle, dropdownWidth, dropdownRight, onOpen, onClose, skipCalendar, defaultDate, headerSlot, onRequestClose }) => {
  const parsed = parseNaive(value);

  const todayRaw = new Date();
  const todayY = todayRaw.getFullYear();
  const todayM = todayRaw.getMonth() + 1;
  const todayD = todayRaw.getDate();


  const [open, setOpen] = useState(false);
  // step: 'calendar' | 'hour' | 'minute'
  const [step, setStep] = useState('calendar');
  const [calYear, setCalYear] = useState(parsed?.year || todayY);
  const [calMonth, setCalMonth] = useState(parsed?.month || todayM);
  const [pickedDate, setPickedDate] = useState(
    parsed ? { year: parsed.year, month: parsed.month, day: parsed.day } : null
  );
  // Выбранный час (на шаге minute)
  const [pickedHour, setPickedHour] = useState(parsed?.hour ?? null);
  // Поле ручного ввода — видно на обоих шагах времени
  const [timeInput, setTimeInput] = useState(
    parsed ? `${pad2(parsed.hour)}:${pad2(parsed.minute)}` : ''
  );
  const [timeError, setTimeError] = useState('');

  const containerRef = useRef(null);
  const timeInputRef = useRef(null);

  // Синхронизация внешнего value
  useEffect(() => {
    const p = parseNaive(value);
    if (p) {
      setPickedDate({ year: p.year, month: p.month, day: p.day });
      setPickedHour(p.hour);
      setTimeInput(`${pad2(p.hour)}:${pad2(p.minute)}`);
      setCalYear(p.year);
      setCalMonth(p.month);
    } else {
      setPickedDate(null);
      setPickedHour(null);
      setTimeInput('');
    }
  }, [value]);

  // Закрытие по клику снаружи
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setStep('calendar');
        setTimeError('');
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Автофокус на поле времени при переходе на шаги времени
  useEffect(() => {
    if ((step === 'hour' || step === 'minute') && open) {
      setTimeout(() => {
        if (timeInputRef.current) {
          timeInputRef.current.focus();
          if (step === 'hour') {
            timeInputRef.current.setSelectionRange(0, 0);
          }
        }
      }, 60);
    }
  }, [step, open]);

  const openPicker = () => {
    // skipCalendar: если задана defaultDate — пропускаем календарь, начинаем с часов
    if (skipCalendar && defaultDate && !parsed) {
      setPickedDate(defaultDate);
      setCalYear(defaultDate.year);
      setCalMonth(defaultDate.month);
      if (pickedHour === null) {
        setTimeInput(':');
      }
      setStep('hour');
    } else {
      setStep(parsed ? 'hour' : 'calendar');
    }
    setOpen(true);
    onOpen?.();
    if (parsed) {
      setTimeout(() => {
        if (timeInputRef.current) {
          timeInputRef.current.focus();
          timeInputRef.current.setSelectionRange(0, 0);
        }
      }, 60);
    }
  };

  const prevMonth = () => {
    if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const newDate = { year: calYear, month: calMonth, day };
    setPickedDate(newDate);
    if (pickedHour === null) {
      setTimeInput(':');
    }
    setStep('hour');
    // Курсор на начало (на часах)
    setTimeout(() => {
      if (timeInputRef.current) {
        timeInputRef.current.focus();
        timeInputRef.current.setSelectionRange(0, 0);
      }
    }, 30);
  };

  // Определяем позицию курсора → переключаем step между 'hour' и 'minute'
  const handleTimeInputSelect = (e) => {
    const pos = e.target.selectionStart;
    const colonIdx = timeInput.indexOf(':');
    if (colonIdx === -1) {
      setStep('hour');
    } else {
      // До двоеточия (включительно позиция 0,1,2) — часы; после — минуты
      setStep(pos <= colonIdx ? 'hour' : 'minute');
    }
  };

  const handleTimeInputChange = (e) => {
    let raw = e.target.value.replace(/[^0-9:]/g, '');
    if (raw.length === 2 && !raw.includes(':') && !timeInput.endsWith(':')) {
      raw = raw + ':';
    }
    if (raw.length > 5) raw = raw.slice(0, 5);
    setTimeInput(raw);
    setTimeError('');

    // Обновляем pickedHour если ввели полное время
    const t = parseTimeInput(raw);
    if (t) setPickedHour(t.hour);

    // Переключаем step по позиции курсора
    const pos = e.target.selectionStart;
    const colonIdx = raw.indexOf(':');
    if (colonIdx !== -1) {
      setStep(pos <= colonIdx ? 'hour' : 'minute');
    }
  };

  const handleConfirm = () => {
    if (!pickedDate) return;
    const t = parseTimeInput(timeInput);
    if (!t) {
      setTimeError('Введите время в формате ЧЧ:ММ, например 17:30');
      return;
    }
    onChange(formatNaive({ ...pickedDate, hour: t.hour, minute: t.minute }));
    setOpen(false);
    setStep('calendar');
    setTimeError('');
    onClose?.();
  };

  const handleHourChipClick = (h) => {
    setPickedHour(h);
    const newVal = `${pad2(h)}:`;
    setTimeInput(newVal);
    setTimeError('');
    setStep('minute');
    setTimeout(() => {
      if (timeInputRef.current) {
        timeInputRef.current.focus();
        timeInputRef.current.setSelectionRange(3, 3);
      }
    }, 30);
  };

  const handleMinuteChipClick = (m) => {
    if (!pickedDate || pickedHour === null) return;
    const h = pickedHour;
    setTimeInput(`${pad2(h)}:${pad2(m)}`);
    setTimeError('');
    onChange(formatNaive({ ...pickedDate, hour: h, minute: m }));
    setOpen(false);
    setStep('calendar');
    onClose?.();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setPickedDate(null);
    setPickedHour(null);
    setTimeInput('');
    setTimeError('');
  };

  // Варианты минут
  const MINUTE_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  // Компактный формат: "17 мая  14:30"
  const MONTHS_SHORT = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  const displayValue = parsed
    ? `${pad2(parsed.day)} ${MONTHS_SHORT[parsed.month - 1]}  ${pad2(parsed.hour)}:${pad2(parsed.minute)}`
    : '';

  const days = buildCalendarDays(calYear, calMonth);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* ── Trigger ── */}
      <div
        onClick={openPicker}
        style={{
          ...inputBase,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none',
          ...(error ? inputError : {}),
          ...(open ? { borderColor: '#6366f1' } : {}),
          ...(triggerStyle || {}),
        }}
      >
        <span style={{ color: displayValue ? '#111827' : '#9ca3af', fontSize: triggerStyle ? 13 : 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {displayValue || placeholder}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {value && (
            <span
              onClick={handleClear}
              style={{
                width: 18, height: 18, borderRadius: '50%', background: '#e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: '#6b7280', fontSize: 12, fontWeight: 700, lineHeight: 1,
              }}
            >×</span>
          )}
          {!triggerStyle && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          )}
        </div>
      </div>

      {/* ── Dropdown ── */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)',
          ...(dropdownRight ? { right: 0 } : { left: 0 }),
          width: dropdownWidth || '100%',
          background: '#fff', borderRadius: 14, border: '1.5px solid #e5e7eb',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)', zIndex: 1000, overflow: 'hidden',
        }}>

          {/* Слот для доп. контента вверху (напр. подсказки длительности) */}
          {headerSlot && (
            <div style={{ borderBottom: '1px solid #f3f4f6' }}>
              {typeof headerSlot === 'function'
                ? headerSlot(() => { setOpen(false); setStep('calendar'); onClose?.(); })
                : headerSlot}
            </div>
          )}

          {/* ══ ШАГ 1: КАЛЕНДАРЬ ══ */}
          {step === 'calendar' && (
            <div style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <button type="button" onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', color: '#374151', fontSize: 18, lineHeight: 1 }}>‹</button>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                  {MONTHS_RU[calMonth - 1]} {calYear}
                </span>
                <button type="button" onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', color: '#374151', fontSize: 18, lineHeight: 1 }}>›</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
                {DAYS_SHORT_RU.map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#9ca3af', padding: '2px 0' }}>{d}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {days.map((day, i) => {
                  if (!day) return <div key={`e-${i}`} />;
                  const isToday = day === todayD && calMonth === todayM && calYear === todayY;
                  const isSelected = pickedDate && day === pickedDate.day && calMonth === pickedDate.month && calYear === pickedDate.year;
                  const isPast = new Date(calYear, calMonth - 1, day) < new Date(todayY, todayM - 1, todayD);
                  return (
                    <button type="button" key={day} onClick={() => !isPast && handleDayClick(day)} style={{
                      width: '100%', aspectRatio: '1', borderRadius: 8, border: 'none',
                      background: isSelected ? '#6366f1' : isToday ? '#ede9fe' : 'transparent',
                      color: isSelected ? '#fff' : isPast ? '#d1d5db' : isToday ? '#6366f1' : '#111827',
                      fontSize: 13, fontWeight: isSelected || isToday ? 700 : 400,
                      cursor: isPast ? 'default' : 'pointer', padding: 0, transition: 'background 0.1s',
                    }}>
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══ ШАГ 2+3: ВВОД ВРЕМЕНИ (час → минуты по курсору) ══ */}
          {(step === 'hour' || step === 'minute') && pickedDate && (
            <div style={{ padding: 16 }}>
              {/* Шапка */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <button type="button"
                  onClick={() => setStep('calendar')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 16, padding: 0, lineHeight: 1 }}
                >←</button>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  {pad2(pickedDate.day)}.{pad2(pickedDate.month)}.{pickedDate.year}
                </span>
                <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
                  {step === 'hour' ? 'Выберите час' : 'Выберите минуты'}
                </span>
              </div>

              {/* Поле ввода времени + кнопка Готово */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <input
                    ref={timeInputRef}
                    type="text"
                    inputMode="numeric"
                    placeholder="17:30"
                    value={timeInput}
                    onChange={handleTimeInputChange}
                    onSelect={handleTimeInputSelect}
                    onClick={handleTimeInputSelect}
                    onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                    style={{
                      ...inputBase,
                      fontSize: 16, fontWeight: 600, letterSpacing: 2,
                      textAlign: 'center',
                      borderColor: timeError ? '#fca5a5' : '#6366f1',
                      background: timeError ? '#fff7f7' : '#fff',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = timeError ? '#ef4444' : '#6366f1'; }}
                    onBlur={(e) => { e.target.style.borderColor = timeError ? '#fca5a5' : '#e5e7eb'; }}
                  />
                  {timeError && (
                    <span style={{ fontSize: 11, color: '#dc2626', display: 'block', marginTop: 4 }}>
                      {timeError}
                    </span>
                  )}
                </div>
                <button type="button"
                  onClick={handleConfirm}
                  title="Готово"
                  style={{
                    width: 42, height: 42, borderRadius: 10, border: 'none', flexShrink: 0,
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    color: '#fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </button>
              </div>

              {/* Чипы часов */}
              {step === 'hour' && (
                <>
                  <p style={{ margin: '0 0 7px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Часы
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 5 }}>
                    {Array.from({ length: 24 }, (_, h) => {
                      const isActive = pickedHour === h;
                      return (
                        <button type="button" key={h} onClick={() => handleHourChipClick(h)} style={{
                          padding: '7px 0',
                          borderRadius: 8,
                          border: `1.5px solid ${isActive ? '#6366f1' : '#e5e7eb'}`,
                          background: isActive ? '#6366f1' : '#f9fafb',
                          color: isActive ? '#fff' : '#111827',
                          fontSize: 13, fontWeight: 600,
                          cursor: 'pointer', transition: 'all 0.1s',
                        }}>
                          {pad2(h)}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Чипы минут */}
              {step === 'minute' && (
                <>
                  <p style={{ margin: '0 0 7px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Минуты
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 5 }}>
                    {MINUTE_OPTIONS.map(m => {
                      const t = parseTimeInput(timeInput);
                      const isActive = t && t.hour === pickedHour && t.minute === m;
                      return (
                        <button type="button" key={m} onClick={() => handleMinuteChipClick(m)} style={{
                          padding: '7px 0',
                          borderRadius: 8,
                          border: `1.5px solid ${isActive ? '#6366f1' : '#e5e7eb'}`,
                          background: isActive ? '#6366f1' : '#f9fafb',
                          color: isActive ? '#fff' : '#111827',
                          fontSize: 13, fontWeight: 600,
                          cursor: 'pointer', transition: 'all 0.1s',
                        }}>
                          :{pad2(m)}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};